import styled from 'styled-components';
import { ButtonBorderRadius } from '~/components/ui/Button';
import { Text } from '~/components/ui/Text';

const StyledBackButton = styled.button<{ $hover?: boolean }>`
	position: absolute;
	top: 50%;
	right: 55px;
	transform: translateY(-50%);
	background-color: ${(props) =>
		props.$hover ? 'rgba(255,255,255,0.1)' : 'transparent'};
	border: 1px solid rgba(255, 255, 255, 0.2);
	width: auto;
	height: 32px;
	padding: 0 12px;
	display: flex;
	align-items: center;
	justify-content: center;
	border-radius: ${ButtonBorderRadius.Radius5}px;
	cursor: pointer;
	transition: all 0.2s ease;

	&:hover {
		background-color: rgba(255, 255, 255, 0.1);
		border-color: rgba(255, 255, 255, 0.4);
	}
`;

const ContentContainer = styled.div`
	flex: 1;
	overflow-y: auto;
	overflow-x: visible;
	padding: 20px;
`;

const StyledLayout = styled.div`
	width: 100%;
	height: 100%;
	display: flex;
	flex-direction: column;
`;

const StyledListSection = styled.div`
	margin-bottom: 20px;
`;

const StyledListSectionTitle = styled(Text)`
	font-size: 14px;
	font-weight: 600;
	color: rgba(255, 255, 255, 0.7);
	margin-bottom: 10px;
	text-transform: uppercase;
	letter-spacing: 0.5px;
`;

const StyledListColumn = styled.div`
	display: flex;
	flex-direction: column;
	gap: 8px;
	width: 100%;
	box-sizing: border-box;
`;

// Table-based promo card styles
const StyledPromoTable = styled.table`
	width: 100%;
	border-collapse: separate;
	border-spacing: 0 6px;
	table-layout: auto;
`;

const StyledTableRow = styled.tr`
	background-color: rgba(255, 255, 255, 0.03);
	border: 1px solid rgba(255, 255, 255, 0.08);
	border-radius: 8px;
`;

const StyledTableCell = styled.td`
	padding: 12px 16px;
	vertical-align: middle;
	white-space: nowrap;
	position: relative;
	overflow: visible;

	&:not(:first-child) {
		padding-left: 0;
	}
`;

const StyledStatusCell = styled.td`
	white-space: nowrap;
	vertical-align: middle;
	width: 1%;
	padding: 12px 16px;
	padding-left: 0;
	display: table-cell;
`;

const StyledPromoCode = styled.span`
	font-family: 'GT Walsheim Pro', monospace;
	font-size: 14px;
	font-weight: 600;
	color: #ffffff;
	background-color: rgba(255, 255, 255, 0.1);
	padding: 4px 10px;
	border-radius: 4px;
	display: inline-block;
`;

const StyledStatusBadge = styled.div<{ $active?: boolean }>`
	display: flex;
	justify-content: center;
	align-items: center;
	height: 20px;
	padding: 0 8px;
	border-radius: 5px;
	background-color: ${(props) =>
		props.$active ? 'rgba(46, 204, 113, 0.15)' : 'rgba(231, 76, 60, 0.15)'};
	border: 1px solid
		${(props) =>
			props.$active ? 'rgba(46, 204, 113, 0.25)' : 'rgba(231, 76, 60, 0.25)'};
	position: relative;

	${Text} {
		font-weight: 500;
		font-size: 11px;
		color: ${(props) => (props.$active ? '#2ecc71' : '#e74c3c')};
		white-space: nowrap;
	}
`;

const StyledPromoRewardsContainer = styled.div`
	display: flex;
	align-items: center;
	gap: 8px;
	width: 100%;
	min-width: 0;
	overflow-x: auto;
	scrollbar-width: none;
	-ms-overflow-style: none;

	&::-webkit-scrollbar {
		display: none;
	}
`;

const StyledRewardsCell = styled.td`
	width: 100%;
	vertical-align: middle;
	padding: 12px 16px;
	padding-left: 0;
`;

const StyledText = styled(Text)`
	font-family: 'GT Walsheim Pro';
	font-size: 12px;
	color: rgba(255, 255, 255, 0.5);
	white-space: nowrap;
`;

const StyledActivationCell = styled.td`
	white-space: nowrap;
	vertical-align: middle;
	width: 1%;
	padding: 12px 16px;
	padding-left: 0;
	display: table-cell;
`;

const StyledTimeCell = styled.td`
	white-space: nowrap;
	vertical-align: middle;
	width: 1%;
	padding: 12px 16px;
	padding-left: 0;
	display: table-cell;
`;

const StyledRewardImageWrapper = styled.div`
	position: relative;
	display: inline-block;
`;

const StyledRewardImage = styled.div`
	width: 36px;
	height: 36px;
	border-radius: 6px;
	overflow: hidden;
	background-color: rgba(255, 255, 255, 0.03);
	display: flex;
	align-items: center;
	justify-content: center;

	img {
		width: 100%;
		height: 100%;
		object-fit: contain;
	}
`;

// Hint/Tooltip styles (based on QuestPromocodeItem pattern)
const StyledHintContainer = styled.div`
	position: absolute;
	bottom: calc(100% + 8px);
	left: 50%;
	transform: translateX(-50%);
	display: flex;
	align-items: center;
	gap: 5px;
	padding: 8px 12px;
	background-color: rgba(255, 255, 255, 0.95);
	border-radius: 8px;
	opacity: 0;
	transition: opacity 0.3s ease;
	white-space: nowrap;
	z-index: 1000;

	font-family: 'GT Walsheim Pro';
	font-weight: 400;
	font-size: 13px;
	color: #000000;

	&.hint-enter {
		opacity: 0;
	}

	&.hint-enter-active,
	&.hint-enter-done,
	&.hint-exit {
		opacity: 1;
	}

	&.hint-exit-active {
		opacity: 0;
	}
`;

const StyledRewardHintContainer = styled(StyledHintContainer)`
	bottom: auto;
	top: calc(100% + 8px);
`;

const StyledButtonsRow = styled.div`
	display: flex;
	align-items: center;
	gap: 10px;
	justify-content: flex-end;
`;

const StyledDeleteButton = styled.button<{ $hover?: boolean }>`
	background-color: ${(props) => (props.$hover ? '#ffffff' : 'transparent')};
	border: none;
	width: 26px;
	height: 26px;
	display: flex;
	align-items: center;
	justify-content: center;
	border-radius: 6px;
	cursor: pointer;
	transition: background-color 0.3s ease;
	position: relative;
	flex-shrink: 0;

	&::before {
		content: '';
		position: absolute;
		width: 12px;
		height: 2px;
		background-color: ${(props) =>
			props.$hover ? '#000000' : 'rgba(255, 255, 255, 0.5)'};
		transform: rotate(45deg);
		transition: background-color 0.3s ease;
	}

	&::after {
		content: '';
		position: absolute;
		width: 12px;
		height: 2px;
		background-color: ${(props) =>
			props.$hover ? '#000000' : 'rgba(255, 255, 255, 0.5)'};
		transform: rotate(-45deg);
		transition: background-color 0.3s ease;
	}

	&:hover {
		background-color: #ffffff;

		&::before,
		&::after {
			background-color: #000000;
		}
	}
`;

const StyledInputLabel = styled(Text)`
	font-size: 13px;
	font-weight: 500;
	color: rgba(255, 255, 255, 0.8);
	margin-bottom: 6px;
	display: block;
`;

const StyledValidationError = styled(Text)`
	font-size: 11px;
	color: #e74c3c;
	margin-top: 4px;
`;

const StyledLoadingText = styled(Text)`
	font-family: 'GT Walsheim Pro';
	font-size: 14px;
	color: rgba(255, 255, 255, 0.6);
	text-align: center;
	padding: 40px;
`;

const StyledEmptyText = styled(Text)`
	font-family: 'GT Walsheim Pro';
	font-size: 14px;
	color: rgba(255, 255, 255, 0.5);
	text-align: center;
	padding: 40px;
`;

export {
	ContentContainer,
	StyledActivationCell,
	StyledBackButton,
	StyledButtonsRow,
	StyledDeleteButton,
	StyledEmptyText,
	StyledHintContainer,
	StyledInputLabel,
	StyledLayout,
	StyledListColumn,
	StyledListSection,
	StyledListSectionTitle,
	StyledLoadingText,
	StyledPromoCode,
	StyledPromoRewardsContainer,
	StyledPromoTable,
	StyledRewardHintContainer,
	StyledRewardImage,
	StyledRewardImageWrapper,
	StyledRewardsCell,
	StyledStatusBadge,
	StyledStatusCell,
	StyledTableCell,
	StyledTableRow,
	StyledText,
	StyledTimeCell,
	StyledValidationError,
};
